import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const PART_REQUEST_SUBJECT = encodeURIComponent("Special Order / Part Request");

/**
 * "Parts We Source" strip — showcases the parts Top Gear tracks down for
 * customers (ECUs, computer boxes and hard-to-find bits) and drives enquiries
 * by routing straight to the contact form with the special-order subject
 * pre-selected.
 */
export default function PartsWeSource() {
  const { t } = useTranslation();
  const { site } = useSiteSettings();

  const items = [
    {
      icon: "ri-cpu-line",
      title: t("partsWeSource.ecuTitle"),
      text: t("partsWeSource.ecuText"),
    },
    {
      icon: "ri-hard-drive-2-line",
      title: t("partsWeSource.computerBoxTitle"),
      text: t("partsWeSource.computerBoxText"),
    },
    {
      icon: "ri-search-eye-line",
      title: t("partsWeSource.hardToFindTitle"),
      text: t("partsWeSource.hardToFindText"),
    },
  ];

  return (
    <section className="w-full bg-neutral-50 py-16 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-brand-gold/15 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-brand-gold">
            <i className="ri-inbox-archive-line"></i>
            {t("partsWeSource.badge")}
          </span>
          <h2 className="mt-4 font-heading text-3xl md:text-4xl font-semibold uppercase text-brand-dark">
            {t("partsWeSource.title")}
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm md:text-base text-neutral-600 leading-relaxed">
            {t("partsWeSource.desc", { siteName: site.site_name })}
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <div
              key={item.title}
              className="flex flex-col rounded-lg border border-neutral-200 bg-white p-7 text-center transition hover:border-brand-gold"
            >
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-dark text-brand-gold">
                <i className={`${item.icon} text-2xl`}></i>
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold uppercase text-brand-dark">
                {item.title}
              </h3>
              <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <Link
            to={`/contact-us?subject=${PART_REQUEST_SUBJECT}`}
            className="inline-flex items-center gap-2 rounded-md bg-brand-gold px-7 py-3 text-sm font-semibold uppercase tracking-wide text-brand-dark transition hover:bg-brand-dark hover:text-white whitespace-nowrap"
          >
            <i className="ri-send-plane-line"></i>
            {t("partsWeSource.cta")}
          </Link>
        </div>
      </div>
    </section>
  );
}